import { datadogRum } from '@datadog/browser-rum';
import { FILE_TYPE_MISMATCH_ERROR } from 'platform/forms-system/src/js/utilities/file';
import recordEvent from 'platform/monitoring/record-event';

import {
  DOC_TYPE_ERROR,
  DUPLICATE_FILE_ERROR,
  PASSWORD_ERROR,
  VALIDATION_ERROR,
} from '../constants';
import { isIntentToFileExpiringSoon, roundToNearest } from './helpers';
import {
  FILE_SIZE_ERROR_NON_PDF,
  FILE_SIZE_ERROR_PDF,
  FILE_SIZE_ERROR_SC,
} from './validations';

/**
 * Analytics Event Schema for Upload Events
 *
 * All upload-related events follow a standardized structure for consistent
 * querying in Google Analytics. Each event populates relevant fields and
 * sets others to undefined.
 *
 * @typedef {Object} UploadAnalyticsEvent
 * @property {string} event - Event name (e.g., 'claims-upload-start', 'claims-upload-success')
 * @property {string|undefined} api-name - Human-readable API identifier
 * @property {string|undefined} api-request-id - Joins one submit attempt's events,
 *   in GA and in DataDog. One id can span several requests, or none at all when
 *   validation blocks the submit — see generateBatchId.
 * @property {string|undefined} api-status - 'started' | 'successful' | 'failed' | 'cancel' | 'blocked'
 * @property {string|undefined} error-key - Error code/identifier when applicable
 * @property {number|undefined} upload-batch-duration-ms - // Measured from when the submit button is
 *  clicked to when the last file finishes. Per-file numbers are `upload-file-duration-ms` on
 *  `claims-upload-file` in Datadog
 * @property {number|undefined} upload-cancel-file-count - Number of files being canceled
 * @property {number|undefined} upload-fail-alert-count - Count of Type 2 failure alerts visible
 * @property {number|undefined} upload-fail-file-count - Number of files that failed upload
 * @property {number|undefined} upload-file-count - Total files in upload batch
 * @property {boolean|undefined} upload-retry - Whether this upload includes retry attempts
 * @property {number|undefined} upload-retry-file-count - Number of files being retried
 * @property {number|undefined} upload-success-file-count - Number of files successfully uploaded
 */

/**
 * Analytics Event Schema for Intent to File Events
 *
 * @typedef {Object} IntentToFileAnalyticsEvent
 * @property {string} event
 * @property {string} api-name - Human-readable API identifier
 * @property {string} api-status - 'successful' | 'failed'
 * @property {number|undefined} api-latency-ms - API call duration in milliseconds
 * @property {string|undefined} error-key - Error reason when applicable
 * @property {boolean} itf-none - True when the response contains no ITFs
 * @property {number} itf-expiring-count - Count of ITFs expiring soon
 * @property {number} itf-not-expiring-count - Count of ITFs not yet expiring
 */

// `api-name` values
export const BENEFITS_DOCUMENTS_API_NAME = 'Claims and Appeals Upload';
export const CLAIMS_EVIDENCE_API_NAME = 'Supplemental Claim Evidence Upload';

/**
 * Base metadata keys for all upload analytics events.
 * Ensures consistent structure across all events for easier querying.
 * @type {Partial<UploadAnalyticsEvent>}
 */
// Every key is enumerated, including ones only some events populate: the GTM
// dataLayer persists across pushes, so a key omitted from one event resolves to
// whatever a previous push left there. Without `api-request-id` listed here, a
// second attempt's start event would report the first attempt's id.
const BASE_UPLOAD_EVENT_KEYS = {
  'api-name': undefined,
  'api-request-id': undefined,
  'api-status': undefined,
  'error-key': undefined,
  'upload-batch-duration-ms': undefined,
  'upload-cancel-file-count': undefined,
  'upload-fail-alert-count': undefined,
  'upload-fail-file-count': undefined,
  'upload-file-count': undefined,
  'upload-retry': undefined,
  'upload-retry-file-count': undefined,
  'upload-success-file-count': undefined,
};

/**
 * Creates an upload analytics event with standardized structure.
 * @param {string} eventName - The event name
 * @param {Partial<UploadAnalyticsEvent>} data - Event-specific data to merge with base keys
 * @returns {UploadAnalyticsEvent} The complete event object
 */
const createUploadEvent = (eventName, data) => ({
  event: eventName,
  ...BASE_UPLOAD_EVENT_KEYS,
  ...data,
});

/**
 * Sends one upload event to Google Analytics and to DataDog.
 *
 * Both sinks get the same payload, so anything on the event reaches both. Pass
 * `ddOnly` for the exceptions — values worth having in DataDog that would change
 * an existing GA number.
 *
 * @param {UploadAnalyticsEvent} event - Built by createUploadEvent; its `event` names the action
 * @param {Object} [ddOnly] - Extra keys for the DataDog payload only
 */
const sendUploadEvent = (event, ddOnly = {}) => {
  recordEvent(event);
  datadogRum.addAction(event.event, { ...event, ...ddOnly });
};

/**
 * Base metadata keys for all ITF analytics events.
 * Ensures consistent structure across all events for easier querying.
 */
const BASE_ITF_EVENT_KEYS = {
  'api-name': undefined,
  'api-status': undefined,
  'api-latency-ms': undefined,
  'error-key': undefined,
  'itf-none': false,
  'itf-expiring-count': 0,
  'itf-not-expiring-count': 0,
};

/**
 * Creates an intent-to-file analytics event with standardized structure.
 * @param {Object} data - Event-specific data to merge with base keys
 * @returns {Object} The complete event object
 */
const createIntentToFileEvent = data => ({
  event: 'claims-itf-status',
  ...BASE_ITF_EVENT_KEYS,
  'api-name': 'GET intents to file',
  ...data,
});

// Session storage keys for upload tracking
const STORAGE_KEYS = {
  UPLOAD_ATTEMPTS: 'cst_upload_attempts',
  FAILED_UPLOADS: 'cst_failed_uploads',
};

// Time-to-live for session storage entries (2 hours in milliseconds)
const TIME_TO_LIVE = 2 * 60 * 60 * 1000;
// Fallback value for missing document type in analytics tracking
const UNKNOWN_DOC_TYPE = 'Unknown';
/**
 * Generates a unique document instance ID
 * @returns {string} Unique identifier for a document upload attempt
 */
export const generateDocInstanceId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers that don't support crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Generates an id for one submit attempt, carried as `api-request-id` by every
 * event that attempt produces, in GA and in DataDog.
 *
 * Generated per submit rather than per upload, so a submission blocked by
 * validation still has one even though it never reaches the uploader. Which also
 * means one id can span several requests, or none at all — despite the field
 * name, it identifies an attempt, not a request.
 *
 * Never sent to vets-api, so no server-side record holds it.
 *
 * @returns {string} Unique identifier for one submit attempt
 */
export const generateBatchId = () => generateDocInstanceId();

/**
 * Gets a storage item with TTL checking
 * @param {string} key - Storage key
 * @returns {any} Stored data or null if expired/not found
 */
const getStorageWithTTL = key => {
  try {
    const item = sessionStorage.getItem(key);

    if (!item) return null;

    const { data, timestamp } = JSON.parse(item);
    const now = Date.now();
    // Check if expired
    if (now - timestamp > TIME_TO_LIVE) {
      sessionStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (error) {
    // If there's any error parsing, clear the item
    sessionStorage.removeItem(key);
    return null;
  }
};

/**
 * Sets a storage item with TTL
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 */
const setStorageWithTTL = (key, data) => {
  try {
    const item = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    // Silently fail if storage is full or unavailable
    // eslint-disable-next-line no-console
    console.warn('Failed to set session storage:', error);
  }
};

/**
 * Creates a file fingerprint for retry detection
 *
 * Uses extension + size + lastModified timestamp to uniquely identify files
 * without storing any PII.
 *
 * Collision only occurs if two different files have the exact same size AND
 * were modified at the exact same millisecond - extremely unlikely in practice.
 *
 * @param {File} file - File object
 * @returns {string} Fingerprint string (format: extension_size_timestamp)
 * @example
 * // Returns: "pdf_156789_1733356900000"
 * createFileFingerprint(myFile)
 */
const createFileFingerprint = file => {
  const extension = file.name.split('.').pop();
  const timestamp = file.lastModified || 0;

  return `${extension}_${file.size}_${timestamp}`;
};

/**
 * Stores a document upload attempt in sessionStorage for retry tracking
 * @param {Object} params - Upload attempt parameters
 * @param {string} params.docInstanceId - Document instance ID
 * @param {File} params.file - File being uploaded
 * @param {string} params.docType - Document type
 * @param {string} params.claimId - Claim ID
 */
export const storeUploadAttempt = ({
  docInstanceId,
  file,
  docType,
  claimId,
}) => {
  const attempts = getStorageWithTTL(STORAGE_KEYS.UPLOAD_ATTEMPTS) || {};
  const fingerprint = createFileFingerprint(file);

  if (!attempts[claimId]) {
    attempts[claimId] = {};
  }

  attempts[claimId][fingerprint] = {
    docInstanceId,
    docType,
    attempts: (attempts[claimId][fingerprint]?.attempts || 0) + 1,
    lastAttempt: Date.now(),
  };

  setStorageWithTTL(STORAGE_KEYS.UPLOAD_ATTEMPTS, attempts);
};

/**
 * Stores a failed upload in sessionStorage for retry tracking
 * @param {Object} params - Failed upload parameters
 * @param {string} params.docInstanceId - Document instance ID
 * @param {File} params.file - File that failed
 * @param {string} params.docType - Document type
 * @param {string} params.claimId - Claim ID
 * @param {string} params.errorCode - Error code if available
 */
export const storeFailedUpload = ({
  docInstanceId,
  file,
  docType,
  claimId,
  errorCode,
}) => {
  const failedUploads = getStorageWithTTL(STORAGE_KEYS.FAILED_UPLOADS) || {};
  const fingerprint = createFileFingerprint(file);

  if (!failedUploads[claimId]) {
    failedUploads[claimId] = {};
  }

  failedUploads[claimId][fingerprint] = {
    docInstanceId,
    docType,
    errorCode,
    failedAt: Date.now(),
  };

  setStorageWithTTL(STORAGE_KEYS.FAILED_UPLOADS, failedUploads);
};

/**
 * Checks if a file upload is a retry attempt
 * @param {File} file - File being uploaded
 * @param {string} claimId - Claim ID
 * @returns {Object} Retry information { isRetry, retryCount, previousDocInstanceId }
 */
export const checkIfRetry = (file, claimId) => {
  const attempts = getStorageWithTTL(STORAGE_KEYS.UPLOAD_ATTEMPTS) || {};
  const fingerprint = createFileFingerprint(file);
  const attemptData = attempts[claimId]?.[fingerprint];

  if (!attemptData) {
    return {
      isRetry: false,
      retryCount: 0,
      previousDocInstanceId: null,
    };
  }

  return {
    isRetry: attemptData.attempts > 0,
    retryCount: attemptData.attempts,
    previousDocInstanceId: attemptData.docInstanceId,
  };
};

/**
 * Counts how many of the files currently uploading were also sent in an
 * earlier upload.
 *
 * Call only after recordUploadStartEvent, which has already recorded the
 * upload in progress. So one stored attempt means "this upload only", and
 * two or more means the file was sent before.
 *
 * @param {Array<Object>} files - Files being uploaded, each shaped { file }
 * @param {string} claimId - Claim ID
 * @returns {number} How many of those files were sent in an earlier upload
 */
export const countInFlightRetries = (files, claimId) =>
  files.filter(fileData => checkIfRetry(fileData.file, claimId).retryCount > 1)
    .length;

/**
 * Clears upload tracking data for a specific claim
 * @param {string} claimId - Claim ID
 */
export const clearUploadTracking = claimId => {
  const attempts = getStorageWithTTL(STORAGE_KEYS.UPLOAD_ATTEMPTS) || {};
  const failedUploads = getStorageWithTTL(STORAGE_KEYS.FAILED_UPLOADS) || {};

  if (attempts[claimId]) {
    delete attempts[claimId];
    setStorageWithTTL(STORAGE_KEYS.UPLOAD_ATTEMPTS, attempts);
  }

  if (failedUploads[claimId]) {
    delete failedUploads[claimId];
    setStorageWithTTL(STORAGE_KEYS.FAILED_UPLOADS, failedUploads);
  }
};

/**
 * Records a Type 2 failure event for claims list page
 * Counts total number of slim alerts
 * Fires on every page visit when alerts are visible
 * @param {Object} params - Event parameters
 * @param {number} params.count - Number of slim alerts visible
 */
export const recordType2FailureEvent = ({ count }) => {
  const event = createUploadEvent('claims-upload-failure-type-2', {
    'api-name': 'Claims and Appeals Upload Fail Type 2 Alert',
    'upload-fail-alert-count': count,
  });
  // No batch id: this is a claims-list page view, not a submit attempt.
  sendUploadEvent(event);
};

/**
 * @param {string} [fileName]
 * @returns {string|null}
 */
const getFileExtension = fileName => {
  if (!fileName?.includes('.')) {
    return null;
  }

  return fileName.split('.').pop().toLowerCase();
};

/**
 * Records one individual file upload (either success or failure) to DataDog
 * Uses `api-request-id` to match it back to a batch of files at submission (if there were multiple)
 *
 * `upload-file-duration-ms` starts when submit button is clicked and stops when uploading finishes
 * A cancelled file does not log. A file rejected before the request will have no `upload-file-duration-ms`
 *
 * Does not log file name; extension only
 *
 * @param {Object} params - Event parameters
 * @param {boolean} params.succeeded - Whether the API accepted the file
 * @param {number} [params.durationMs] - Milliseconds from request start to settle; absent if it never started
 * @param {string} [params.errorKey] - Parsed `DOC_UPLOAD_*` code, or 'UNKNOWN'; failures only
 * @param {number} [params.status] - HTTP status from the failed request; failures only
 * @param {string} [params.docType] - Document type label
 * @param {File} [params.file] - The file; only extension and size are read
 * @param {string} [params.batchId] - Joins this attempt's events, as `api-request-id`
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordFileUploadEvent = ({
  succeeded,
  durationMs,
  errorKey,
  status,
  docType,
  file,
  batchId,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  datadogRum.addAction('claims-upload-file', {
    'api-name': apiName,
    'api-status': succeeded ? 'successful' : 'failed',
    'upload-file-duration-ms': durationMs,
    'doc-type': docType || UNKNOWN_DOC_TYPE,
    'file-extension': getFileExtension(file?.name),
    'file-size': file?.size,
    ...(errorKey ? { 'error-key': errorKey } : {}),
    ...(status ? { 'http-status': status } : {}),
    ...(batchId ? { 'api-request-id': batchId } : {}),
  });
};

/**
 * Records a client-side validation error to DataDog only.
 *
 * Validation errors (file too large, wrong type, missing password, missing
 * doc type, no files selected) are caught before the upload POST, so they
 * never reach the existing upload-failure path. This event surfaces them
 * in DataDog RUM so the dashboard can distinguish the cause of failure
 * without requiring a server-side round-trip.
 *
 * Intentionally not mirrored to Google Analytics — DD-only scope per
 * ticket #143392.
 *
 * @param {Object} params - Event parameters
 * @param {string} params.errorKey - A key returned by getValidationErrorKey
 * @param {number} [params.fileCount] - Number of files involved (optional)
 * @param {string} [params.phase] - When the error surfaced: 'add' (file
 *   added/replaced) or 'submit' (blocked on submission)
 * @param {string} [params.apiName] - `api-name`: labels the event for either
 *   the Benefits Documents or Claims Evidence API
 */
export const recordValidationErrorEvent = ({
  errorKey,
  fileCount,
  phase,
  duplicateReason,
  batchId,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  datadogRum.addAction('claims-upload-validation-error', {
    'api-name': apiName,
    'error-key': errorKey,
    'upload-file-count': fileCount,
    ...(phase ? { phase } : {}),
    // Both duplicate kinds share one message, so the source can't be derived
    // from the key — it has to be passed in. Attributes are free here: no
    // registration, no cardinality limit, and no dataLayer to leak into.
    ...(duplicateReason ? { 'duplicate-reason': duplicateReason } : {}),
    ...(batchId ? { 'api-request-id': batchId } : {}),
  });
};

// Maps a user-facing validation error message to a stable analytics key. The
// lookup is on the message text, so every wording needs its own entry or it
// falls through to 'UNKNOWN'.
//
// Benefits Documents splits its size limit by file type, so its two messages
// keep two keys. Claims Evidence caps every file at the PDF limit and says so
// in one message, so it gets one key that doesn't claim a distinction the
// check no longer makes; `api-name` on the event separates the flows.
const VALIDATION_ERROR_KEYS = {
  [FILE_SIZE_ERROR_PDF]: 'FILE_SIZE_TOO_LARGE_PDF',
  [FILE_SIZE_ERROR_NON_PDF]: 'FILE_SIZE_TOO_LARGE_NON_PDF',
  [FILE_SIZE_ERROR_SC]: 'FILE_SIZE_TOO_LARGE',
  [FILE_TYPE_MISMATCH_ERROR]: 'FILE_TYPE_MISMATCH',
  [PASSWORD_ERROR]: 'MISSING_PASSWORD',
  [DOC_TYPE_ERROR]: 'MISSING_DOC_TYPE',
  [VALIDATION_ERROR]: 'NO_FILES_SELECTED',
  // Supplemental Claims only — the benefit claims flow has no duplicate check.
  [DUPLICATE_FILE_ERROR]: 'DUPLICATE_FILE',
};

const getValidationErrorKey = message =>
  VALIDATION_ERROR_KEYS[message] || 'UNKNOWN';

/**
 * Records a submit attempt blocked by front-end validation (does not reach the API)
 * Reported as `claims-upload-failure` with `api-status: 'blocked'`
 * One event per submission attempt, not per file
 *
 * @param {Object} params - Event parameters
 * @param {Array<string>} params.errorMessages - Blocking messages, sparse by file index
 * @param {number} params.fileCount - Files in the batch
 * @param {string} [params.batchId] - Joins this attempt's events
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordBlockedSubmitEvent = ({
  errorMessages = [],
  fileCount,
  batchId,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  const blocking = errorMessages.filter(Boolean);

  if (!blocking.length) return;

  const event = createUploadEvent('claims-upload-failure', {
    'api-name': apiName,
    'api-request-id': batchId,
    'api-status': 'blocked',
    // One key for a batch that may have several reasons. The full set is in the
    // per-file DataDog stream; this names the first so the funnel has a reason.
    'error-key': getValidationErrorKey(blocking[0]),
    'upload-fail-file-count': blocking.length,
  });
  // DataDog only. No other claims-upload-failure sets upload-file-count, so in GA
  // this count would stand alone in that event's file-count total.
  sendUploadEvent(event, {
    'upload-file-count': fileCount,
  });
};

/**
 * Records one DataDog validation-error event per non-empty error message,
 * mapping each message to a stable error key. No-ops on an empty list.
 *
 * @param {Array<string>} [errorMessages] - Validation error messages
 * @param {number} [fileCount] - Number of files involved (optional)
 * @param {string} [phase] - When the errors surfaced: 'add' or 'submit'
 * @param {string} [apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordValidationErrors = (
  errorMessages = [],
  fileCount,
  phase,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
  { duplicateReasons = [], batchId } = {},
) => {
  // Iterated with the index rather than filtered first, so `duplicateReasons`
  // stays aligned with the file each message belongs to.
  errorMessages.forEach((message, index) => {
    if (!message) return;

    recordValidationErrorEvent({
      errorKey: getValidationErrorKey(message),
      fileCount,
      phase,
      duplicateReason: duplicateReasons[index],
      batchId,
      apiName,
    });
  });
};

/**
 * Records an upload start event
 * Also stores upload attempts in sessionStorage for retry tracking
 * @param {Object} params - Event parameters
 * @param {Array<Object>} params.files - Files being uploaded
 * @param {string} params.claimId - Claim ID
 * @param {string} [params.batchId] - Joins this attempt's events, as `api-request-id`
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordUploadStartEvent = ({
  files,
  claimId,
  batchId,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  // Check if any files are retries
  const filesWithRetryInfo = files.map(fileData => {
    const retryInfo = checkIfRetry(fileData.file, claimId);
    const docInstanceId = generateDocInstanceId();
    // Note: Frontend validation ensures docType is always present before submission,
    // but we use a fallback for defensive programming to ensure
    // analytics never breaks the upload flow if validation logic changes.
    const docType = fileData.docType?.value || UNKNOWN_DOC_TYPE;
    // Store this attempt in sessionStorage for retry tracking
    storeUploadAttempt({
      docInstanceId,
      file: fileData.file,
      docType,
      claimId,
    });

    return {
      docInstanceId,
      retryInfo,
      docType,
    };
  });

  // Count how many files are retries and sum total retry attempts
  const retryFileCount = filesWithRetryInfo.filter(
    fileInfo => fileInfo.retryInfo.isRetry,
  ).length;

  const event = createUploadEvent('claims-upload-start', {
    'api-name': apiName,
    'api-request-id': batchId,
    'api-status': 'started',
    'upload-file-count': files.length,
    'upload-retry': retryFileCount > 0,
    'upload-retry-file-count': retryFileCount,
  });
  sendUploadEvent(event);

  return { filesWithRetryInfo, retryFileCount };
};

/**
 * Records an upload failure event (Type 1)
 * Also stores failed upload data in sessionStorage for retry tracking
 * @param {Object} params - Event parameters
 * @param {Array<Object>} params.errorFiles - Files that failed; `fileIndex` on an
 *   entry gives its position in `files`, since this list holds failures only
 * @param {Array<Object>} params.files - Original files array (for matching file objects)
 * @param {Array<Object>} params.filesWithRetryInfo - Files with retry info from start event
 * @param {string} params.claimId - Claim ID
 * @param {number} params.retryFileCount - Number of files that were retries
 * @param {number} [params.successFileCount] - Files that succeeded in the same batch (partial-success case)
 * @param {string} [params.batchId] - Joins this attempt's events, as `api-request-id`
 * @param {number} [params.durationMs] - Wall-clock from submit to batch settle, unrounded
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordUploadFailureEvent = ({
  errorFiles,
  files,
  filesWithRetryInfo,
  claimId,
  retryFileCount,
  successFileCount,
  batchId,
  durationMs,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  // Store each failed upload in sessionStorage for retry tracking.
  //
  // Positional lookup only works while errorFiles and files are the same length.
  // Supplemental Claims pushes one entry per failure, so a partial-failure batch
  // misaligns them and would fingerprint a file that succeeded. `fileIndex`
  // carries the real position; the fallback keeps the claim flow, which builds
  // errorFiles the same way, on its existing behaviour.
  errorFiles.forEach((error, index) => {
    const fileIndex = error?.fileIndex ?? index;
    const fileInfo = filesWithRetryInfo?.[fileIndex];
    const fileObject = files?.[fileIndex]?.file;

    if (fileInfo && fileObject) {
      const errorCode = error?.errors?.[0]?.detail || UNKNOWN_DOC_TYPE;

      storeFailedUpload({
        docInstanceId: fileInfo.docInstanceId,
        file: fileObject,
        docType: error.docType || UNKNOWN_DOC_TYPE,
        claimId,
        errorCode,
      });
    }
  });

  const event = createUploadEvent('claims-upload-failure', {
    'api-name': apiName,
    'api-request-id': batchId,
    'api-status': 'failed',
    'error-key': errorFiles[0]?.errors?.[0]?.detail || UNKNOWN_DOC_TYPE,
    'upload-batch-duration-ms': durationMs,
    'upload-fail-file-count': errorFiles.length,
    'upload-retry': retryFileCount > 0,
    'upload-retry-file-count': retryFileCount,
  });
  // DataDog only. A count of what landed is useful for reading a partial-batch
  // failure, but adding it to GA would change what an existing failure report
  // shows — this event already reports the failures, and the claim flow emits it
  // too.
  sendUploadEvent(event, {
    'upload-success-file-count': successFileCount,
  });
};

/**
 * Records an enhanced upload success event to Google Analytics
 * @param {Object} params - Event parameters
 * @param {number} params.fileCount - Number of files uploaded
 * @param {number} params.retryFileCount - Number of files that were retries
 * @param {string} [params.batchId] - Joins this attempt's events, as `api-request-id`
 * @param {number} [params.durationMs] - Wall-clock from submit to batch settle, unrounded
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordUploadSuccessEvent = ({
  fileCount,
  retryFileCount,
  batchId,
  durationMs,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  const event = createUploadEvent('claims-upload-success', {
    'api-name': apiName,
    'api-request-id': batchId,
    'api-status': 'successful',
    'upload-batch-duration-ms': durationMs,
    'upload-retry': retryFileCount > 0,
    'upload-retry-file-count': retryFileCount,
    'upload-success-file-count': fileCount,
  });
  sendUploadEvent(event);
};

/**
 * Records an upload cancel event to Google Analytics
 * @param {Object} params - Event parameters
 * @param {number} params.cancelFileCount - Number of files being canceled
 * @param {number} params.retryFileCount - Number of canceled files that were retries
 * @param {string} [params.batchId] - Joins this attempt's events, as `api-request-id`
 * @param {string} [params.apiName] - `api-name` dimension; defaults to Benefits Documents
 */
export const recordUploadCancelEvent = ({
  cancelFileCount,
  retryFileCount,
  batchId,
  apiName = BENEFITS_DOCUMENTS_API_NAME,
}) => {
  const event = createUploadEvent('claims-upload-cancel', {
    'api-name': apiName,
    'api-request-id': batchId,
    'api-status': 'cancel',
    'upload-cancel-file-count': cancelFileCount,
    'upload-retry': retryFileCount > 0,
    'upload-retry-file-count': retryFileCount,
  });
  sendUploadEvent(event);
};

/**
 * Records a claim letters API call event after `/claim_letters` resolves or fails.
 * @param {Object} params
 * @param {boolean} params.success - Whether the call succeeded
 * @param {string} [params.error] - Error reason key (set on failure)
 * @param {number} [params.startTime] - Date.now() captured before the call, for latency
 */
export const recordClaimLettersApiEvent = ({ success, error, startTime }) => {
  const event = {
    event: 'api_call',
    'api-name': 'GET claim_letters',
    'api-status': success ? 'successful' : 'failed',
  };
  if (error) {
    event['error-key'] = error;
  }
  if (startTime) {
    event['api-latency-ms'] = roundToNearest({
      interval: 5000,
      value: Date.now() - startTime,
    });
  }
  // GA4 keys use underscores; include both delimiters (matches recordClaimsAPIEvent).
  Object.keys(event).forEach(key => {
    if (key.includes('-')) {
      event[key.replace(/-/g, '_')] = event[key];
    }
  });
  recordEvent(event);
  if (event['error-key']) {
    recordEvent({ 'error-key': undefined });
  }
};

/**
 * Records an ITF API call event after `/intents_to_file` resolves or fails.
 * On success, includes expiring/not-expiring counts derived from the response.
 * @param {Object} params
 * @param {Array<{ expirationDate: string }>} [params.intentsToFile=[]] - Resolved ITF data when `errorKey` is omitted (may be empty)
 * @param {number} params.latencyMs - API call duration in milliseconds
 * @param {string} [params.errorKey] - Error reason (omit on success; when set, `intentsToFile` is ignored)
 */
export const recordIntentToFilePageViewEvent = ({
  intentsToFile = [],
  latencyMs,
  errorKey,
}) => {
  if (errorKey) {
    recordEvent(
      createIntentToFileEvent({
        'api-status': 'failed',
        'api-latency-ms': latencyMs,
        'error-key': errorKey,
      }),
    );
    return;
  }

  let expiringCount = 0;
  let notExpiringCount = 0;

  for (const itf of intentsToFile) {
    if (isIntentToFileExpiringSoon(itf.expirationDate)) {
      expiringCount += 1;
    } else {
      notExpiringCount += 1;
    }
  }

  recordEvent(
    createIntentToFileEvent({
      'api-status': 'successful',
      'api-latency-ms': latencyMs,
      'itf-none': intentsToFile.length === 0,
      'itf-expiring-count': expiringCount,
      'itf-not-expiring-count': notExpiringCount,
    }),
  );
};

/**
 * Records an appeal detail tab activation to DataDog only. Tab switches change
 * only ?tab=, so RUM creates no view for them; this keeps them measurable.
 *
 * @param {Object} params
 * @param {string} params.tab - The activated tab id (e.g. 'status', 'issues')
 */
export const recordAppealTabChangeEvent = ({ tab }) => {
  datadogRum.addAction('appeal-tab-change', { tab });
};

/**
 * Records a claim detail tab activation to DataDog only. Tab switches change
 * only ?tab=, so RUM creates no view for them; this keeps them measurable.
 *
 * @param {Object} params
 * @param {string} params.tab - The activated tab id ('status'|'files'|'overview')
 */
export const recordClaimTabChangeEvent = ({ tab }) => {
  datadogRum.addAction('claim-tab-change', { tab });
};

/**
 * Records which summary box a first-party request page shows: "You responded
 * to this request" or "The deadline to respond to this request has passed".
 * Leaves out the request name, since some request names are sensitive.
 *
 * @param {Object} params
 * @param {string} params.heading - 'Responded' | 'Past due'
 */
export const recordRequestSummaryBoxEvent = ({ heading }) => {
  recordEvent({
    event: 'int-summary-box',
    // eslint-disable-next-line camelcase
    heading_1: heading,
  });
};
